// 사람에게 보여 줄 오류 문구의 «최후 방어선» — 서버·본체 앱·관리자 콘솔 공통.
//
// 배경(2026-10-15): 정책 자동화 → 액면 업로드에서 처리가 실패하자, «처리 결과» 칸에
// `Failed query: insert into "policy_rows" (...) params: 791,갤럭시 Z Flip8,...` 같은 DB 오류 원문이
// 파라미터 수백 개째로 떴다. 서버가 예외 원문(err.message)을 실패 사유로 저장했고, 화면은
// 그 칸을 그대로 그렸다. 판매점 직원은 무엇이 잘못됐는지도, 무엇을 해야 하는지도 알 수 없었다.
//
// 원칙
//   - 출처에서 사람 말로 바꾸는 게 1차다. 이 모듈은 그걸 놓쳤을 때 걸러 내는 2차 그물이다.
//   - 서버가 일부러 쓴 한국어 안내(예: «비밀번호가 걸린 파일이라 읽을 수 없습니다…»)는 그대로
//     통과시킨다 — 원문 문자열로 분기하는 코드(isPolicyPasswordProtectedFileError 등)가 있어서다.
//   - «기술 문장처럼 보이는 글»(SQL·DB 오류·스택·영어 라이브러리 메시지·한글 없는 문장)은
//     호출부가 주는 상황별 안내 문장(fallback)으로 바꾼다. 원문은 버리지 말고 로그·Sentry·Slack 으로.

/** 기술 오류 흔적. 하나라도 걸리면 사람에게 보이면 안 되는 원문으로 본다. */
const TECHNICAL_PATTERNS: readonly RegExp[] = [
  // DB(Drizzle·Postgres·Supabase)
  /failed query/i,
  /\bparams:/i,
  /\b(insert\s+into|delete\s+from|select\s+.+\s+from|update\s+\S+\s+set)\b/i,
  /violates\s+.*constraint/i,
  /duplicate key/i,
  /\bPGRST\d+/,
  /relation\s+".+"\s+does not exist/i,
  /column\s+".+"\s+(does not exist|of relation)/i,
  /syntax error at or near/i,
  /deadlock detected/i,
  /canceling statement/i,
  /connection (terminated|refused|reset)/i,
  /too many clients/i,
  /\bUNSAFE_TRANSACTION\b/,
  /\bEMAXCONN/,
  // 네트워크·런타임(Node·브라우저)
  /\bE(CONNREFUSED|CONNRESET|TIMEDOUT|NOTFOUND|PIPE|AI_AGAIN|NOENT|ACCES|PERM|HOSTUNREACH)\b/,
  /\bUND_ERR/,
  /failed to fetch/i,
  /fetch failed/i,
  /networkerror/i,
  /\bload failed\b/i, // Safari 의 fetch 실패
  /socket hang up/i,
  /signal is aborted|signal timed out|operation was aborted|user aborted|aborted a request|\bAbortError\b/i,
  /document is not focused|\bNotAllowedError\b|write permission denied|permission denied/i, // 클립보드 등 브라우저 권한
  /\bchunk\b.*\bfailed\b|dynamically imported module/i,
  /\b(TypeError|ReferenceError|SyntaxError|RangeError|ZodError|AxiosError)\b/,
  /cannot read propert/i,
  /is not a function/i,
  /is not defined/i,
  /unexpected (token|end of json)/i,
  /\bJSON\.parse\b|is not valid json/i,
  /timeout of \d+\s*ms/i,
  /request failed with status code/i,
  /\bstatus code \d{3}\b/i,
  /^\s*HTTP\s*\d{3}/i,
  /\b\d{3}\s+(Bad Request|Unauthorized|Forbidden|Not Found|Internal Server Error|Bad Gateway|Service Unavailable|Gateway Timeout|Too Many Requests)\b/i,
  // LLM·외부 API 원문
  /GoogleGenerativeAI|generativelanguage\.googleapis/i,
  /\b(RESOURCE_EXHAUSTED|DEADLINE_EXCEEDED|PERMISSION_DENIED|UNAVAILABLE|INVALID_ARGUMENT)\b/,
  /\b(AccessDenied|NoSuchKey|NoSuchBucket|ThrottlingException|ValidationException)\b/,
  /\braw=/, // LLM 응답 원문 덤프(`… / raw={…`)
  // Zod·JSON 덤프·HTML 오류 페이지
  /"code"\s*:\s*"/,
  /invalid_type|expected .+, received /i,
  /^\s*[{[]/,
  /<!doctype|<html/i,
  // 스택·예외 꼬리표
  /\bat\s+\S+\s+\(?\S+:\d+:\d+\)?/,
  /\b[A-Za-z]*(Error|Exception):\s/,
  /\bnode_modules\b|\.(ts|js|mjs|cjs):\d+/,
];

/** 오류 문구라기엔 너무 긴 글 — 덤프일 가능성이 높다. 서버가 쓰는 안내는 이보다 훨씬 짧다. */
const MAX_HUMAN_MESSAGE_LENGTH = 400;

const HANGUL = /[가-힣]/;

/**
 * 한국어 문장 뒤에 영어 오류 문장이 붙은 꼴(`사용자 생성 실패: User already registered`)을 잡는다.
 * 영어 단어 3개 이상이 이어지고 그중 소문자 단어가 2개 이상이면 문장으로 본다 — `Galaxy Buds Live`
 * 같은 모델명(단어마다 대문자)은 걸리지 않게.
 */
function hasEnglishSentence(msg: string): boolean {
  const runs = msg.match(/[A-Za-z]+(?:[ ,'-]+[A-Za-z]+){2,}/g) ?? [];
  return runs.some((run) => run.split(/[ ,'-]+/).filter((w) => /^[a-z]{2,}$/.test(w)).length >= 2);
}

/**
 * 이 글이 «개발자용 오류 원문»처럼 보이면 true.
 * 빈 글, 한글이 하나도 없는 글(영어 라이브러리 메시지·내부 코드), 기술 흔적이 있는 글,
 * 한국어 뒤에 영어 오류 문장이 붙은 글, 지나치게 긴 글.
 */
export function looksTechnicalMessage(text: string | null | undefined): boolean {
  const msg = (text ?? "").trim();
  if (!msg) return true;
  if (!HANGUL.test(msg)) return true;
  if (msg.length > MAX_HUMAN_MESSAGE_LENGTH) return true;
  if (hasEnglishSentence(msg)) return true;
  return TECHNICAL_PATTERNS.some((re) => re.test(msg));
}

/**
 * 사람에게 보여 줄 문구를 고른다. 사람 말이면 그대로, 기술 원문이면 fallback(상황별 안내).
 * DB 에 이미 원문으로 저장된 옛 실패 사유를 화면에 그릴 때도 이걸 거친다.
 */
export function toUserFacingText(text: string | null | undefined, fallback: string): string {
  return looksTechnicalMessage(text) ? fallback : (text ?? "").trim();
}

/** 예외 객체 버전 — Error 면 message, 문자열이면 그 자체를 보고 판정한다. */
export function toUserFacingError(err: unknown, fallback: string): string {
  const text =
    err instanceof Error
      ? err.message
      : typeof err === "string"
        ? err
        : typeof (err as { message?: unknown })?.message === "string"
          ? (err as { message: string }).message
          : "";
  return toUserFacingText(text, fallback);
}

/** 상황별 안내 문장 — 같은 말을 여러 곳에서 쓰도록 모아 둔다. 끝에 고객이 할 일을 붙인다. */
export const USER_FACING_FALLBACK = {
  /** 처리 중 시스템 오류(우리 쪽 문제) — 알림이 실제로 가는 경로에서만 쓴다. */
  systemFailureReported: "처리 중 시스템 오류가 났어요. 개발팀에 자동으로 알렸어요. 잠시 후 다시 시도해 주세요.",
  /** 처리 중 시스템 오류(알림 여부 불명). */
  systemFailure: "처리 중 일시적인 오류가 났어요. 잠시 후 다시 시도해 주세요. 계속되면 문의해 주세요.",
  /** 저장 실패. */
  saveFailed: "저장하지 못했어요. 잠시 후 다시 시도해 주세요. 계속되면 문의해 주세요.",
  /** 불러오기 실패. */
  loadFailed: "정보를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.",
  /** 정책표 업로드 처리 실패(알림이 가는 경로). */
  policyUploadFailedReported:
    "정책표를 처리하던 중 시스템 오류가 났어요. 개발팀에 자동으로 알렸어요. 잠시 후 같은 파일을 다시 올려 주세요.",
  /** 정책표 업로드 처리 실패(옛 기록 — 알림 여부 불명). */
  policyUploadFailed: "정책표를 처리하지 못했어요. 잠시 후 같은 파일을 다시 올려 주시고, 계속되면 문의해 주세요.",
} as const;
